// ============================================================================
// TRAINING GUIDE - PM Knowledge Guide with AI Assistant
// ============================================================================

import { useState, useEffect, useCallback } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { knowledgeApi, type PMTopic } from '@/lib/api/client'
import { TrainingGuideSidebar } from './TrainingGuideSidebar'
import { TrainingGuideContent } from './TrainingGuideContent'
import { TrainingGuideAssistant } from './TrainingGuideAssistant'
import { BookOpen, MessageCircle, X, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'

export function TrainingGuide() {
  const { accountId } = useSessionStore()
  const [topics, setTopics] = useState<PMTopic[]>([])
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAssistant, setShowAssistant] = useState(false)
  const [isBackfilling, setIsBackfilling] = useState(false)
  const [backfillMessage, setBackfillMessage] = useState<string | null>(null)

  // Fetch topics with counts
  const fetchTopics = useCallback(async () => {
    if (!accountId) return

    try {
      setLoading(true)
      setError(null)
      const response = await knowledgeApi.getTopics(accountId)
      setTopics(response.topics)

      // Select first topic with content by default
      const firstWithContent = response.topics.find(t => t.chunkCount > 0)
      if (firstWithContent) {
        setSelectedTopicId(firstWithContent.id)
      } else if (response.topics.length > 0) {
        setSelectedTopicId(response.topics[0].id)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load topics')
    } finally {
      setLoading(false)
    }
  }, [accountId])

  // Fetch topics on mount
  useEffect(() => {
    fetchTopics()
  }, [fetchTopics])

  // Backfill topic classification for unclassified chunks
  const handleBackfill = useCallback(async () => {
    if (!accountId || isBackfilling) return

    setIsBackfilling(true)
    setBackfillMessage('Classifying knowledge chunks...')

    try {
      let totalClassified = 0
      let hasMore = true

      // Process in batches until done
      while (hasMore) {
        const result = await knowledgeApi.backfillTopics(accountId, 500)
        totalClassified += result.classified
        hasMore = result.hasMore
        setBackfillMessage(`Classified ${totalClassified} chunks...`)
      }

      setBackfillMessage(`Done! Classified ${totalClassified} chunks.`)

      // Refresh topics to show updated counts
      await fetchTopics()

      // Clear message after 3 seconds
      setTimeout(() => setBackfillMessage(null), 3000)
    } catch (err) {
      setBackfillMessage(`Error: ${err instanceof Error ? err.message : 'Backfill failed'}`)
      setTimeout(() => setBackfillMessage(null), 5000)
    } finally {
      setIsBackfilling(false)
    }
  }, [accountId, isBackfilling, fetchTopics])

  const selectedTopic = topics.find(t => t.id === selectedTopicId)
  const totalChunks = topics.reduce((sum, t) => sum + t.chunkCount, 0)

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-warm-100">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-market border-t-transparent rounded-full mx-auto mb-3"></div>
          <p className="text-text-secondary text-sm">Loading training guide...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-warm-100">
        <div className="text-center">
          <p className="text-error mb-2">{error}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex overflow-hidden bg-warm-100">
      {/* Sidebar Navigation */}
      <TrainingGuideSidebar
        topics={topics}
        selectedTopicId={selectedTopicId}
        onSelectTopic={setSelectedTopicId}
        totalChunks={totalChunks}
        onBackfill={handleBackfill}
        isBackfilling={isBackfilling}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-8">
          {/* Backfill Message Banner */}
          {backfillMessage && (
            <div className="mb-4 bg-market/10 border border-market/20 rounded-lg p-3 flex items-center gap-3">
              {isBackfilling && (
                <RefreshCw className="w-4 h-4 text-market animate-spin" />
              )}
              <span className="text-sm text-market">{backfillMessage}</span>
            </div>
          )}

          {selectedTopic ? (
            <TrainingGuideContent
              topic={selectedTopic}
              accountId={accountId!}
            />
          ) : (
            <EmptyState
              totalChunks={totalChunks}
              onBackfill={handleBackfill}
              isBackfilling={isBackfilling}
            />
          )}
        </div>
      </div>

      {/* AI Assistant Toggle Button */}
      <Button
        onClick={() => setShowAssistant(!showAssistant)}
        className={`fixed bottom-6 right-6 rounded-full w-14 h-14 shadow-lg z-40 ${
          showAssistant ? 'bg-warm-200 text-text hover:bg-warm-300' : 'bg-market text-white hover:bg-market/90'
        }`}
      >
        {showAssistant ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </Button>

      {/* AI Assistant Panel */}
      {showAssistant && (
        <TrainingGuideAssistant
          onClose={() => setShowAssistant(false)}
          currentTopic={selectedTopic}
        />
      )}
    </div>
  )
}

interface EmptyStateProps {
  totalChunks: number
  onBackfill: () => void
  isBackfilling: boolean
}

function EmptyState({ totalChunks, onBackfill, isBackfilling }: EmptyStateProps) {
  return (
    <div className="text-center py-12">
      <div className="w-16 h-16 rounded-xl bg-market/10 flex items-center justify-center mx-auto mb-4">
        <BookOpen className="w-8 h-8 text-market" />
      </div>
      <h2 className="text-xl font-semibold text-text mb-2">Training Guide</h2>
      {totalChunks === 0 ? (
        <>
          <p className="text-text-secondary mb-4">
            No training content has been classified yet.
          </p>
          <p className="text-sm text-text-muted mb-6">
            If you have processed knowledge sources, click below to classify them into topics.
          </p>
          <Button
            onClick={onBackfill}
            disabled={isBackfilling}
            className="bg-market hover:bg-market/90 text-white"
          >
            {isBackfilling ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Classifying...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Classify Knowledge
              </>
            )}
          </Button>
        </>
      ) : (
        <p className="text-text-secondary">
          Select a topic from the sidebar to view related knowledge.
        </p>
      )}
    </div>
  )
}

export default TrainingGuide
